import { styled } from '@mui/material/styles';
import { useState } from 'react';

import creditCard from '@/assets/images/landingPages/adventCalendar/icons/credit_card_icon.svg';
import snowflakes from '@/assets/images/landingPages/adventCalendar/snowflakes.svg';
import type { AdventWinner } from '@/competitions/2023/december/pages/LandingPage/schemas.ts';
import ConfirmModal from '@/components/ConfirmModal/index.ts';

import ModalContent from './_components/ModalContent/index.ts';

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  lineHeight: 1.15,
  borderRadius: '20px',
  overflow: 'hidden',
  backgroundColor: isOpen
    ? theme.palette.background.christmasGreen
    : theme.palette.background.christmasBlue,
}));

const DayOpenTextTitle = styled('p')(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.common.white,
  fontSize: '1rem',
  fontWeight: 400,
  paddingBottom: theme.spacing(0.5),
  [theme.breakpoints.down('lg')]: {
    fontSize: '0.75rem',
  },
}));

const DayOpenTextDescription = styled('p')(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.common.white,
  fontSize: '1.438rem',
  fontWeight: 700,
  paddingBottom: theme.spacing(0.5),
  [theme.breakpoints.down('lg')]: {
    fontSize: '1rem',
  },
}));

const OpenDayPill = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: '3px 18px',
  borderRadius: '24px',
  backgroundColor: '#336E67',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationColor: theme.palette.common.white,
  },
  [theme.breakpoints.down('lg')]: {
    padding: '3px 9px',
    marginBottom: theme.spacing(0.5),
    fontSize: '0.7rem',
  },
}));

const OpenDayPillText = styled('p')(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.common.white,
  fontSize: '0.9rem',
  [theme.breakpoints.down('lg')]: {
    fontSize: '0.7rem',
  },
}));

const CreditCardIcon = styled('img')(({ theme }) => ({
  width: '45px',
  height: '45px',
  position: 'relative',
  paddingBottom: theme.spacing(0.5),
  [theme.breakpoints.down('lg')]: {
    width: '32px',
    height: '20px',
  },
}));

const Snowflakes = styled('img')(({ theme }) => ({
  display: 'none',
  width: '270px',
  height: '100px',
  position: 'absolute',
  transform: 'translate(0%, -20%)',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
  [theme.breakpoints.down('md')]: {
    width: '200px',
    height: '80px',
    transform: 'translate(0%, -10%)',
  },
}));

interface AdventDayProps {
  day: number;
  winners: AdventWinner[];
  className?: string;
}

const AdventDay = ({ day, winners, className }: AdventDayProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dayTitle = `${day}. desember`;
  const amount = winners[0]?.meta.amount;
  const hasWinners = winners.length > 0;

  return (
    <Container className={className} isOpen={hasWinners}>
      <CreditCardIcon src={creditCard} alt="" />
      <DayOpenTextTitle>{dayTitle}</DayOpenTextTitle>
      {amount ? <DayOpenTextDescription>{amount}</DayOpenTextDescription> : null}
      {hasWinners ? (
        <OpenDayPill onClick={() => setIsModalOpen(true)}>
          <OpenDayPillText>Se vinnere</OpenDayPillText>
        </OpenDayPill>
      ) : null}
      {day === 24 ? <Snowflakes src={snowflakes} alt="" /> : null}

      {hasWinners ? (
        <ConfirmModal
          onClose={() => setIsModalOpen(false)}
          isOpen={isModalOpen}
          maxWidth="md"
          background="default"
          modalTitle=""
        >
          <ModalContent
            onClick={() => setIsModalOpen(false)}
            dayTitle={dayTitle}
            winners={winners}
          />
        </ConfirmModal>
      ) : null}
    </Container>
  );
};

export default AdventDay;
